<template>
  <div class="setup-link">
    <Stack>
      <p>Send a link to the Client so they can access the Portal</p>

      <Stack
        class="link-box"
        gap="4"
      >
        <p>Clients will be able to view document checklists, and upload requested documents.</p>

        <template v-if="false">
          <label
            class="option is-selected"
            disabled
          >
            <div class="option-input">
              <input
                type="checkbox"
                checked
                disabled
              >
            </div>

            <Stack
              class="option-body"
              gap="2"
            >
              <header class="option-header">
                <p class="option-title">Allow the Client to access with a YourStake Guest account
                  with username and password</p>
                <Badge status="danger">Required</Badge>
              </header>

              <div class="option-description">
                This guarantees the Client is always able to view document checklists, and upload requested documents.
              </div>
            </Stack>
          </label>

          <label
            class="option is-interactive"
            :class="{ 'is-selected': allowMagicLink }"
          >
            <div class="option-input">
              <input
                v-model="allowMagicLink"
                type="checkbox"
              >
            </div>

            <Stack
              class="option-body"
              gap="3"
            >
              <header class="option-header">
                <p class="option-title">Allow the Client to login in one-click via a Magic Link</p>
                <Badge status="info">Optional</Badge>
              </header>

              <div class="option-description">
                This allows the Client to request a one-click login link sent straight to their inbox.
              </div>
            </Stack>
          </label>

          <Button
            v-if="hasActivated"
            variant="success"
            disabled
          >
            Activated
          </Button>

          <Button
            v-else
            variant="primary"
            :loading="isActivating"
            @click="handleClientPortalActivation"
          >
            Activate the Client Portal
          </Button>
        </template>

        <Stack
          v-if="invitationURL"
          gap="4"
        >
          <div class="portal-activated">
            <p>
              The Client Portal is active at the following URL:
            </p>
          </div>

          <CopyToClipboard
            :value="invitationURL"
          />

          <template v-if="clientPortalStore.portalContext?.client?.email">
            <p>You can also invite the Client via the following email:</p>

            <div
              class="email-box"
              gap="3"
            >
              <Label>From</Label>
              <p class="email-info">
                noreply@yourstake.org
              </p>

              <Label>To</Label>
              <p class="email-info">
                {{ clientPortalStore.portalContext?.client.email }}
              </p>
              <Label>CC</Label>
              <p class="email-info">
                {{ clientPortalStore.portalContext?.advisor?.email }}
              </p>

              <Label>Subject</Label>
              <TextInput
                v-model="clientEmailData.email_subject"
                spacing="tight"
                size="3_5"
              />

              <Label>Message</Label>
              <textarea
                v-model="clientEmailData.email_body_text"
                class="stk-input stk-size-3_5"
                type="textarea"
                rows="16"
              />

              <Button
                class="email-reset"
                icon-before="fas fa-redo-alt"
                size="3"
                @click="handleResetEmail"
              >
                Reset changes
              </Button>

              <Button
                class="email-send"
                :disabled="!invitationURL || !clientPortalStore.portalContext?.client?.email || sendingEmail || !clientEmailData.email_subject || !clientEmailData.email_body_text"
                variant="primary"
                icon-before="fas fa-paper-plane"
                :loading="sendingEmail"
                spacing="loose"
                @click="sendClientPortalEmail"
              >
                Send this email to {{ clientPortalStore.portalContext?.client.email }}
              </Button>
            </div>
          </template>
        </Stack>
      </Stack>
    </Stack>
  </div>
</template>

<script setup>
import Vue, { nextTick, ref, watch } from 'vue';
import {
  Badge,
  Button,
  CopyToClipboard,
  Label,
  Stack,
  TextInput,
} from '@stakeui';
import { useClientPortalStore } from '@frontend/ui/stores/clientPortalStore';

const clientPortalStore = useClientPortalStore();

const props = defineProps({
  entityExternalId: {
    type: String,
    default: '',
  },
});

const csrfToken = ref('');
const invitationURL = ref('');
const clientEmailData = ref({});
const originalEmailData = ref({});
const allowMagicLink = ref(false);
const isActivating = ref(false);
const sendingEmail = ref(false);
const hasActivated = ref(false);
// API

// Handlers
const handleClientPortalActivation = async () => {
  if (isActivating.value || invitationURL.value) {
    return;
  }

  isActivating.value = true;

  const entityExternalId = props.entityExternalId;
  const email = clientPortalStore.portalContext?.client?.email;
  const advisorSlug = clientPortalStore.portalContext?.advisor?.slug;

  if (!entityExternalId || !email || !advisorSlug) {
    return;
  }

  try {
    const requestData = {
      entity_external_id: entityExternalId,
      email,
      advisor_slug: advisorSlug,
    };

    const response = await fetch(

        '/api/v1/client-portal/invite/',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken.value,
          },
          body: JSON.stringify(requestData),
          credentials: 'same-origin',
        },
    );

    if (!response.ok) {
      throw new Error(`Failed to activate client portal: ${response.status}`);
    }

    hasActivated.value = true;

    const data = await response.json();

    if (data.success && data.invitation_url) {
      invitationURL.value = data.invitation_url;
    }
  } catch (err) {
    console.error(`Error activating client portal: ${err.message}`);
  } finally {
    isActivating.value = false;
  }
};

const handleResetEmail = () => {
  if (!originalEmailData.value) {
    return;
  }

  if (!window.confirm('Are you sure? You will lose any changes made.')) {
    return;
  }

  clientEmailData.value.email_subject = originalEmailData.value.email_subject;
  clientEmailData.value.email_body_text = originalEmailData.value.email_body_text;
};

const getClientPortalEmailPreview = async () => {
  const entityExternalId = props.entityExternalId;
  const email = clientPortalStore.portalContext?.client?.email;
  const advisorSlug = clientPortalStore.portalContext?.advisor?.slug;
  const requestData = {
    entity_external_id: entityExternalId,
    email,
    advisor_slug: advisorSlug,
    invitation_url: invitationURL.value,
  };

  try {
    const response = await fetch('/api/v1/client-portal/client-portal-email-template-data/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken.value },
      body: JSON.stringify(requestData),
      credentials: 'same-origin',
    });

    if (response.ok) {
      const data = await response.json();
      Vue.set(clientEmailData.value, 'email_subject', data.email_subject || '');
      Vue.set(clientEmailData.value, 'email_body_text', data.email_body_text || '');
      originalEmailData.value = structuredClone(data);
    }
  } catch (error) {
    console.error('Error getting client portal invite email preview:', error);
  }
};

const sendClientPortalEmail = async () => {
  const entityExternalId = props.entityExternalId;
  const email = clientPortalStore.portalContext?.client?.email;
  const advisorSlug = clientPortalStore.portalContext?.advisor?.slug;

  if (!entityExternalId || !email || !advisorSlug) {
    Vue.prototype.$buefy.toast.open({
      message: `Missing client or advisor details.`,
      type: 'is-danger',
    });
    return;
  }

  sendingEmail.value = true;
  try {
    const requestData = {
      entity_external_id: entityExternalId,
      email, advisor_slug: advisorSlug,
      invitation_url: invitationURL.value,
      email_subject: clientEmailData.value.email_subject,
      email_body_text: clientEmailData.value.email_body_text,
    };
    const response = await fetch('/api/v1/client-portal/send-client-portal-email/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken.value },
      body: JSON.stringify(requestData),
      credentials: 'same-origin',
    });

    const data = await response.json();

    if (response.ok) {
      Vue.prototype.$buefy.toast.open({
        message: `Email sent successfully to ${email}`,
        type: 'is-success',
      });
    } else {
      Vue.prototype.$buefy.toast.open({
        message: data.error || 'Failed to send email.',
        type: 'is-danger',
      });
    }
  } catch (error) {
    console.error('Error sending client portal invite email:', error);
    Vue.prototype.$buefy.toast.open({
      message: `An error occurred while sending the email.`,
      type: 'is-danger',
    });
  } finally {
    sendingEmail.value = false;
  }
};

// Lifecycle
watch(
    () => [
      props.entityExternalId,
      clientPortalStore.portalContext?.client?.email,
      clientPortalStore.portalContext?.advisor?.slug,
    ],
    async ([entityExternalId, email, advisorSlug]) => {
      if (!entityExternalId || !email || !advisorSlug) return;
      if (hasActivated.value || isActivating.value) return;

      await refreshCsrf();
      await handleClientPortalActivation();
    },
    { immediate: true },
);

watch(
    invitationURL,
    async url => {
      if (!url) return;
      await getClientPortalEmailPreview();
    },
    { immediate: true },
);

async function refreshCsrf() {
  if (!csrfToken.value) {
    await nextTick(); // ensures DOM is ready
    csrfToken.value = document.querySelector(
        '[name="csrfmiddlewaretoken"]',
    )?.value || '';
  }
}
</script>

<style scoped>
.setup-link {
  flex-grow: 1;
  margin: 0 auto;
  padding: var(--center-padding);
  max-width: var(--center-max-width);
  width: 100%;
}

.link-box {
  background-color: #fff;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.04),
    0 -1px 0 0 rgba(0, 0, 0, 0.08) inset;
  border-radius: 0.75em;
  padding: 1.5em 1.25em;
  line-height: 1.25;
}

.option {
  display: flex;
  align-items: start;
  border: 1px solid var(--stk-border);
  border-radius: 0.75em;
  gap: 0.5em;
  background-color: #fff;
  padding: 1em;

  &.is-interactive {
    cursor: pointer;
  }

  &:hover {
    box-shadow: 0 0 0 1px var(--stk-border);
  }

  &.is-selected {
    border-color: var(--stk-primary);
    box-shadow: 0 0 0 1px var(--stk-primary), 0 4px 4px 0 rgba(151, 71, 255, 0.10);
  }
}

.option-input {
  display: flex;
  padding: 0.25em;
}

.option-header {
  display: flex;
  align-items: center;
  gap: 0.5em;
  justify-content: space-between;
}

.option-body {
  flex-grow: 1;
}

.option-title {
  font-weight: 500;
  color: var(--stk-text-strong);
}

.portal-activated {
  display: flex;
  align-items: center;
  gap: 1em;
  justify-content: space-between;
  margin-bottom: -0.5em;
}

.email-box {
  border-radius: 0.75em;
  padding: 1em;
  background-color: var(--stk-text-95);
  display: grid;
  grid-template-columns: 6em 1fr;
  gap: 0.5em 1em;
  position: relative;
}

.email-info {
  font-size: 0.875em;
}

.email-send {
  grid-column: 2;
}

.email-preview {
  padding: 1em;
  border-radius: 0.5em;
  background-color: #fff;
}
</style>
